/**
 * FAQ item structure
 */
export interface FAQItem {
  question: string;
  answer: string;
}

/**
 * A missing translation resolves to the key itself; never ship that as content.
 * `ring-pro:` is main's next-i18next form (still produced by the identity `t`
 * fallback); `ring-pro.faq.` is what next-intl's getMessageFallback emits once
 * ringProKeyT has stripped the prefix.
 */
const I18N_KEY_MARKERS = ['ring.faq.', 'ring-pro:', 'ring-pro.faq.'];

export function looksLikeI18nKey(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed) return false;
  return I18N_KEY_MARKERS.some((marker) => trimmed.includes(marker));
}

/** Drops empty or unresolved rows rather than inventing a dummy answer. */
export function toResolvedFaqItems(
  items: Array<{ question: string; answer: string }>,
): FAQItem[] {
  return items.filter((item) => {
    const question = item.question.trim();
    const answer = item.answer.trim();
    if (!question || !answer) return false;
    if (looksLikeI18nKey(question) || looksLikeI18nKey(answer)) return false;
    return true;
  });
}

/**
 * Generates FAQ schema from an array of Q&A pairs
 * Can be used on any page that has FAQ content
 */
export function generateFAQSchema(faqs: FAQItem[]) {
  const mainEntity = faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  }));

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity,
  };
}

/**
 * Converts the FAQ schema to JSON-LD string for injection
 */
export function getFAQSchemaScript(faqs: FAQItem[]): string {
  return JSON.stringify(generateFAQSchema(faqs));
}

/**
 * Helper function to extract FAQs from common data structures
 * Example: from a CMS response or markdown content
 */
export function extractFAQsFromArray(
  data: Array<{ q?: string; a?: string; question?: string; answer?: string }>,
): FAQItem[] {
  return data.map((item) => ({
    question: item.q || item.question || '',
    answer: item.a || item.answer || '',
  }));
}
